import { Suspense } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { LoadingBlock } from '@/components/ui/Spinner';
import { useAuth } from '@/stores/auth.store';
import { NotificationsBell } from './NotificationsBell';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { PORTAL_NAV } from './nav';

/**
 * Portal do cliente: navegação reduzida (painel, aprovações, relatórios,
 * calendário) para os papéis CLIENT_VIEWER e CLIENT_APPROVER.
 */
export function ClientPortalShell() {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const items = PORTAL_NAV.filter((item) => can(item.permissions));

  return (
    <div className="flex h-full flex-col bg-bg">
      <header className="sticky top-0 z-30 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex h-14 w-full max-w-[1200px] items-center gap-3 px-3 sm:px-5">
          <span className="flex items-center gap-2">
            <span
              aria-hidden
              className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-xs font-bold text-primary-fg"
            >
              MO
            </span>
            <span className="hidden text-sm font-semibold text-fg sm:block">Portal do cliente</span>
          </span>

          <div className="ml-auto flex items-center gap-1.5">
            <ThemeToggle className="hidden sm:flex" />
            <NotificationsBell />
            <UserMenu />
          </div>
        </div>

        <nav aria-label="Navegação do portal" className="mx-auto w-full max-w-[1200px] px-1 sm:px-4">
          <ul className="table-scroll flex items-center gap-1">
            {items.map((item) => {
              const Icon = item.icon;
              const active = item.matchPrefix
                ? pathname.startsWith(item.matchPrefix)
                : pathname === item.to;
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={!item.matchPrefix}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'relative flex items-center gap-2 whitespace-nowrap px-3 py-2.5 text-sm transition',
                      active ? 'font-medium text-primary' : 'text-muted hover:text-fg',
                    )}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                    {item.label}
                    {active && (
                      <span aria-hidden className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-primary" />
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[1200px] px-3 py-4 sm:px-5 sm:py-6">
          <Suspense fallback={<LoadingBlock />}>
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  );
}
